export const EMBROIDERY_PROFILE_KEY = 'embroidery_profile';
export const EMBROIDERY_VIEWS = ['front', 'back', 'left', 'right'] as const;
export type EmbroideryViewName = (typeof EMBROIDERY_VIEWS)[number];

export interface EmbroideryViewProfile {
  enabled: boolean;
  defaults: { threadColor: string; density: number; stitchAngle: number; textureOverlay: boolean };
  limits: {
    densityMin: number;
    densityMax: number;
    stitchAngleMin: number;
    stitchAngleMax: number;
    hoopWidthMm: number;
    hoopHeightMm: number;
    minDetailMm: number;
  };
}

export interface EmbroideryProfile {
  version: 1;
  views: Record<EmbroideryViewName, EmbroideryViewProfile>;
}

const HEX = /^#[0-9a-f]{6}$/i;
const objectValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const numberIn = (value: unknown, min: number, max: number, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(min, Math.min(max, parsed)) : fallback;
};

export function defaultEmbroideryViewProfile(): EmbroideryViewProfile {
  return {
    enabled: false,
    defaults: { threadColor: '#1a1a1a', density: 1, stitchAngle: 25, textureOverlay: true },
    limits: {
      densityMin: 0.5, densityMax: 2, stitchAngleMin: 0, stitchAngleMax: 180,
      hoopWidthMm: 200, hoopHeightMm: 200, minDetailMm: 8,
    },
  };
}

export function normalizeEmbroideryViewProfile(raw: unknown): EmbroideryViewProfile {
  const input = objectValue(raw);
  const defaults = objectValue(input.defaults);
  const limits = objectValue(input.limits);
  const base = defaultEmbroideryViewProfile();
  const densityMin = numberIn(limits.densityMin, 0.5, 2, 0.5);
  const densityMax = numberIn(limits.densityMax, densityMin, 2, 2);
  const stitchAngleMin = numberIn(limits.stitchAngleMin, 0, 180, 0);
  const stitchAngleMax = numberIn(limits.stitchAngleMax, stitchAngleMin, 180, 180);
  const color = String(defaults.threadColor ?? '').toLowerCase();
  return {
    enabled: input.enabled === true || input.enabled === 1 || input.enabled === '1',
    defaults: {
      threadColor: HEX.test(color) ? color : base.defaults.threadColor,
      density: numberIn(defaults.density, densityMin, densityMax, base.defaults.density),
      stitchAngle: numberIn(defaults.stitchAngle, stitchAngleMin, stitchAngleMax, base.defaults.stitchAngle),
      textureOverlay: defaults.textureOverlay !== false && defaults.textureOverlay !== 0 && defaults.textureOverlay !== '0',
    },
    limits: {
      densityMin,
      densityMax,
      stitchAngleMin,
      stitchAngleMax,
      hoopWidthMm: numberIn(limits.hoopWidthMm, 1, 2000, 200),
      hoopHeightMm: numberIn(limits.hoopHeightMm, 1, 2000, 200),
      minDetailMm: numberIn(limits.minDetailMm, 0, 100, 8),
    },
  };
}

export function normalizeEmbroideryProfile(raw: unknown): EmbroideryProfile {
  let value = raw;
  if (typeof value === 'string' && value.trim()) {
    try { value = JSON.parse(value); } catch { value = {}; }
  }
  const input = objectValue(value);
  const views = Number(input.version) === 1 ? objectValue(input.views) : {};
  // Core 4 always present; addon-registered views persisted in the raw profile pass through.
  const viewKeys = [...new Set([...EMBROIDERY_VIEWS, ...Object.keys(views)])];
  return {
    version: 1,
    views: Object.fromEntries(
      viewKeys.map((view) => [view, normalizeEmbroideryViewProfile(views[view])]),
    ) as Record<EmbroideryViewName, EmbroideryViewProfile>,
  };
}

export function embroideryViewProfile(raw: unknown, view: string): EmbroideryViewProfile {
  const profile = normalizeEmbroideryProfile(raw);
  return profile.views[view as EmbroideryViewName] ?? profile.views.front;
}

export function embroideryProfileHasEnabledView(raw: unknown): boolean {
  return Object.values(normalizeEmbroideryProfile(raw).views).some((view) => view.enabled);
}

interface PrintingLike {
  printing_code?: string;
  values?: Record<string, unknown> | null;
}

/**
 * Same resolution the server does in `veyop_embroidery_method_pack_for_printing`: an explicit
 * `method_pack`, else the legacy `embroidery` code. Kept local so this module stays free of the
 * extension registry, whose provider list is only populated once an addon bundle has loaded.
 */
function isEmbroideryPrinting(printing: PrintingLike | undefined | null): boolean {
  const explicit = String(printing?.values?.method_pack ?? '').trim();
  if (explicit) return explicit === 'embroidery-pack';
  return String(printing?.printing_code ?? '').trim() === 'embroidery';
}

/**
 * Printing code to preselect so embroidery renders straight away, or '' to keep the default.
 *
 * Only claims the selection when embroidery is the product's *single* print method: with a
 * choice on offer the shopper owns it, and forcing embroidery would also force its surcharge.
 */
export function soleEmbroideryPrintingCode(
  printings: PrintingLike[] | undefined | null,
  rawProfile: unknown,
): string {
  const rows = Array.isArray(printings) ? printings : [];
  if (rows.length !== 1) return '';
  const code = String(rows[0]?.printing_code ?? '').trim();
  if (!code || !isEmbroideryPrinting(rows[0])) return '';
  return embroideryProfileHasEnabledView(rawProfile) ? code : '';
}

export function clampEmbroiderySettings(raw: unknown, profile: EmbroideryViewProfile): Record<string, unknown> {
  const input = objectValue(raw);
  const color = String(input.threadColor ?? profile.defaults.threadColor).toLowerCase();
  return {
    ...input,
    version: 1,
    threadColor: HEX.test(color) ? color : profile.defaults.threadColor,
    density: numberIn(input.density, profile.limits.densityMin, profile.limits.densityMax, profile.defaults.density),
    stitchAngle: numberIn(input.stitchAngle, profile.limits.stitchAngleMin, profile.limits.stitchAngleMax, profile.defaults.stitchAngle),
    textureOverlay: input.textureOverlay == null
      ? profile.defaults.textureOverlay
      : input.textureOverlay !== false && input.textureOverlay !== 0 && input.textureOverlay !== '0',
  };
}
